import React, { useCallback, useEffect, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { NumericFormat } from "react-number-format";

import { ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE } from "constants/ActionTypes";

import { ExternalLink } from "ui/Link";

import { hideModal } from "actions/modal";
import { selectHashtagFeedCollection } from "actions/sourceMedia";
import { addCollectionAssetsFromFeed } from "actions/mediaLibrary";
import { getRoutinesLoading } from "selectors/commonSelectors";
import { getCollections } from "selectors/mediaLibrarySelectors";
import {
  getSelectedHashtagFeedCollection,
  getSelectedHashtag,
} from "selectors/hashtagFeedSelectors";
import { CollectionSelectorModern } from "features/media-library/components/collection-selector/collection-selector-modern";
import { Spinner } from "@/components/ui/spinner";
import type { ReduxRootState } from "types/redux-store";

interface FeedMedia {
  media_url: string;
  username?: string;
  like_count?: number;
  comments_count?: number;
  timestamp?: string;
  caption?: string;
  permalink?: string;
}

interface ViewHashtagFeedItemModalProps {
  media: FeedMedia;
}

const ViewHashtagFeedItemModal: React.FC<ViewHashtagFeedItemModalProps> = ({
  media,
}) => {
  const selectedHashtag = useSelector(getSelectedHashtag);
  const selectedHashtagFeedCollection = useSelector(
    getSelectedHashtagFeedCollection
  );
  const isFetching = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE])
  );
  const collections = useSelector(getCollections);

  const dispatch = useDispatch();

  // Track if we've started saving to detect completion
  const wasSavingRef = useRef(false);

  // Close modal when save completes successfully
  useEffect(() => {
    if (wasSavingRef.current && !isFetching) {
      // Save just completed - close the modal
      // Toast is handled by the saga
      dispatch(hideModal());
    }
    wasSavingRef.current = isFetching;
  }, [isFetching, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const handleCollectionChange = useCallback(
    (collectionId: string | string[]) => {
      const id = Array.isArray(collectionId) ? collectionId[0] : collectionId;
      dispatch(selectHashtagFeedCollection(id));
    },
    [dispatch]
  );

  const submitAssets = useCallback(() => {
    dispatch(
      addCollectionAssetsFromFeed({
        collectionId: selectedHashtagFeedCollection._id,
        feedItems: [media],
        location: "library",
      })
    );
  }, [dispatch, selectedHashtagFeedCollection, media]);

  // Build modal title with context
  const modalTitle = media.username
    ? `Media from @${media.username}`
    : selectedHashtag
      ? `Media from #${selectedHashtag.name}`
      : "Instagram media";

  const canSave = !isEmpty(selectedHashtagFeedCollection) && !isFetching;

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[560px] max-h-[90vh] overflow-hidden p-0"
        showCloseButton={false}
      >
        <div className="flex flex-col max-h-[90vh]">
          {/* Header */}
          <DialogHeader className="shrink-0 px-6 py-3">
            <DialogTitle className="font-gotham text-lg font-semibold text-neutral-900 leading-tight">
              {modalTitle}
            </DialogTitle>
          </DialogHeader>

          {/* Scrollable content */}
          <DialogBody className="flex-1 px-6">
            <div className="space-y-4 pb-6">
              {/* Image preview - full width, constrained height */}
              <div className="flex items-center justify-center bg-neutral-50 rounded-lg overflow-hidden">
                <img
                  src={media.media_url}
                  className="max-h-[400px] w-auto max-w-full object-contain"
                  alt={media.caption || "Instagram media"}
                />
              </div>

              {/* Compact stats row */}
              <div className="flex items-center gap-4 text-sm text-neutral-600">
                <div className="flex items-center gap-1.5">
                  <i className="fa-regular fa-heart text-neutral-400" />
                  <NumericFormat
                    value={media.like_count}
                    displayType="text"
                    thousandSeparator
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="fa-regular fa-comment text-neutral-400" />
                  <NumericFormat
                    value={media.comments_count}
                    displayType="text"
                    thousandSeparator
                  />
                </div>
                {media.timestamp && (
                  <span className="text-neutral-500">
                    {moment(media.timestamp).format("MMM D, YYYY")}
                  </span>
                )}
              </div>

              {/* Caption section */}
              {media.caption && (
                <div className="space-y-1.5">
                  <span className="font-gotham text-sm font-semibold text-neutral-700">
                    Caption
                  </span>
                  <p className="font-inter text-sm text-neutral-600 whitespace-pre-wrap leading-relaxed">
                    {media.caption}
                  </p>
                </div>
              )}

              {/* View on Instagram link - subtle styling */}
              {media.permalink && (
                <ExternalLink href={media.permalink}>
                  <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-700 transition-colors">
                    View on Instagram
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                  </span>
                </ExternalLink>
              )}
            </div>
          </DialogBody>

          {/* Footer */}
          <div className="shrink-0 flex flex-row items-center justify-between border-t border-neutral-200 px-6 py-4">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <div className="flex items-center gap-3">
              <span className="font-inter text-sm text-neutral-600 shrink-0">
                Save to:
              </span>
              <CollectionSelectorModern
                collections={collections}
                selectedCollection={selectedHashtagFeedCollection}
                onChange={handleCollectionChange}
                className="w-[200px]"
              />
              <Button
                variant="blue-primary"
                onClick={submitAssets}
                disabled={!canSave}
              >
                {isFetching ? (
                  <>
                    <Spinner size="sm" variant="inverted" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewHashtagFeedItemModal;
